/**
 * Utility function to seed timeframe options into Firestore
 * This can be run from the browser console while authenticated as an admin
 *
 * Usage:
 * 1. Open browser console while logged in as admin
 * 2. Import and call: seedTimeframes()
 */

import {
  collection,
  getDocs,
  query,
  doc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { LEGACY_TIMEFRAME_OPTIONS } from '../services/disciplineConfigService.js';

/**
 * Check if timeframes already exist in Firestore
 */
export async function timeframesExist() {
  try {
    const q = query(collection(db, 'timeframes'));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking timeframes:', error);
    throw error;
  }
}

/**
 * Seed timeframes into Firestore
 * @param {boolean} force - Force seeding even if timeframes already exist
 * @returns {Promise<Object>} Result object with success status and count
 */
export async function seedTimeframes(force = false) {
  console.log('🚀 Starting timeframes seeding...\n');

  try {
    // Check if data already exists
    const exists = await timeframesExist();
    if (exists && !force) {
      const message = '⚠️  Timeframes already exist in Firestore. Use seedTimeframes(true) to force re-seed.';
      console.warn(message);
      return {
        success: false,
        message,
        count: 0
      };
    }

    // Convert timeframe options to array
    const timeframesToSeed = Object.values(LEGACY_TIMEFRAME_OPTIONS);

    console.log(`📝 Preparing to seed ${timeframesToSeed.length} timeframes:\n`);
    timeframesToSeed.forEach(tf => {
      console.log(`   - ${tf.label} (${tf.value}) - ${tf.days} days`);
    });
    console.log('');

    // Seed the data using batch write
    const batch = writeBatch(db);
    const collectionRef = collection(db, 'timeframes');

    timeframesToSeed.forEach((tfData) => {
      const docRef = doc(collectionRef);
      batch.set(docRef, {
        ...tfData,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    console.log('✅ Seeding completed successfully!\n');
    console.log('Summary:');
    console.log(`   - ${timeframesToSeed.length} timeframes added to Firestore`);
    console.log('   - Collection: timeframes');
    console.log('   - All timeframes set to active: true\n');

    // Verify the seeding
    console.log('🔍 Verifying seeding...');
    const q = query(collectionRef);
    const snapshot = await getDocs(q);
    console.log(`✓ Found ${snapshot.size} timeframes in Firestore\n`);

    console.log('🎉 Seeding complete! You can now:');
    console.log('   1. Use the TimeframesManager UI to manage timeframes');
    console.log('   2. Add new custom timeframes');
    console.log('   3. Edit or deactivate existing timeframes\n');

    return {
      success: true,
      message: 'Timeframes seeded successfully',
      count: timeframesToSeed.length
    };
  } catch (error) {
    console.error('❌ Seeding failed:', error);
    console.error('\nError details:', error.message);
    return {
      success: false,
      message: `Seeding failed: ${error.message}`,
      count: 0,
      error
    };
  }
}

/**
 * Get current timeframes from Firestore
 */
export async function getTimeframesFromFirestore() {
  try {
    const q = query(collection(db, 'timeframes'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching timeframes:', error);
    throw error;
  }
}

// Make available in window for console access
if (typeof window !== 'undefined') {
  window.seedTimeframes = seedTimeframes;
  window.getTimeframesFromFirestore = getTimeframesFromFirestore;
  window.timeframesExist = timeframesExist;
}

export default seedTimeframes;
